import { useMemo, useRef, useCallback } from 'react';
import dayjs from 'dayjs';
import {
  Table, TableBody, TableCell,
  TableContainer, TableHead, 
  TableRow, TableSortLabel, Box
} from '@mui/material';
import { useDateSort } from '../../../../hooks/useDateSort';
import TableActionsMenu from '../../../../components/common/TableActionsMenu';
import {
  PointhomeFunnelRetentionData,
} from '../../../../types/pointhomeFunnel.types';
import type { Dayjs } from 'dayjs';

type Props = {
  data: PointhomeFunnelRetentionData;
  selectedApp?: string | null;
  startDate: Dayjs;
  endDate: Dayjs;
};

export default function PointhomeRetentionTable({ data, selectedApp, startDate, endDate }: Props) {
  // const rowsPerPage = 7;
  // const [page, setPage] = useState(0);
  
  // 테이블 ref - 엑셀 다운로드용
  const tableRef = useRef<HTMLTableElement>(null);

  // 파일명 생성
  const filename = useMemo(() => {
    const appPart = selectedApp && selectedApp !== '앱 전체' ? `_${selectedApp}` : '_전체';
      return `${startDate.format('YYMMDD')}-${endDate.format('YYMMDD')}${appPart}`;
  }, [startDate, endDate, selectedApp]);

  const retentionLabels = useMemo(() => {
    const daySet = new Set<number>();
    data.forEach((row) => {
      const baseDate = dayjs(row.date);
      row.retention?.forEach((entry) => {
        if (!entry?.date) return;
        const offset = dayjs(entry.date).diff(baseDate, 'day');
        if (offset > 0) {
          daySet.add(offset);
        }
      });
    });
    const sortedDays = Array.from(daySet).sort((a, b) => a - b);
    if (sortedDays.length === 0) {
      return [];
    }
    const minDay = Math.max(1, sortedDays[0]);
    const maxDay = sortedDays[sortedDays.length - 1];
    const labels: string[] = [];
    for (let day = minDay; day <= maxDay; day += 1) {
      labels.push(`D${day}`);
    }
    return labels;
  }, [data]);

  // 날짜별로 그룹화하고 총합 계산
  const dateSummariesRaw = useMemo(() => {
    const summary = data.map((row) => {
      const retentionMap: Record<string, number> = {};
      const cashRetentionMap: Record<string, number> = {};
      const baseDate = dayjs(row.date);
      (row.retention ?? []).forEach((entry) => {
        if (!entry?.date) return;
        const offsetDay = dayjs(entry.date).diff(baseDate, 'day');
        if (offsetDay <= 0) return;
        const key = `D${offsetDay}`;
        const baseNewUserCount = row.newUserCount ?? 0;
        const actualCount = entry.count ?? 0;
        retentionMap[key] = actualCount > baseNewUserCount ? baseNewUserCount : actualCount;
      });
      let newUserCashCount = 0;
      (row.cashRetention ?? []).forEach((entry) => {
        if (!entry?.date) return;
        const offsetDay = dayjs(entry.date).diff(baseDate, 'day');
        const baseNewUserCount = row.newUserCount ?? 0;
        const actualCount = entry.count ?? 0;
        if (offsetDay === 0) {
          newUserCashCount = actualCount > baseNewUserCount ? baseNewUserCount : actualCount;
          return;
        }
        if (offsetDay < 0) return;
        const key = `D${offsetDay}`;
        cashRetentionMap[key] = actualCount > baseNewUserCount ? baseNewUserCount : actualCount;
      });
      return {
        date: row.date,
        dau: row.dau,
        newUserCount: row.newUserCount,
        newUserCashCount,
        retention: retentionMap,
        cashRetention: cashRetentionMap,
      };
    });

    return summary;
  }, [data]);

  const { sorted: dateSummaries, sortDirection, toggleSort } = useDateSort(dateSummariesRaw, undefined, 'asc');

  // 페이지네이션된 데이터
  // const paginatedSummaries = useMemo(() => {
  //   const startIndex = page * rowsPerPage;
  //   const endIndex = startIndex + rowsPerPage;
  //   return dateSummaries.slice(startIndex, endIndex);
  // }, [dateSummaries, page, rowsPerPage]);

  const formatNumber = useCallback(
    (value: number) => Math.round(value).toLocaleString('ko-KR'),
    []
  );

  const formatRetentionWithRate = useCallback(
    (count: number, newUserCount?: number) => {
      const base = newUserCount ?? 0;
      const displayCount = formatNumber(count);
      if (base <= 0) return displayCount;
      const rate = (count / base) * 100;
      return `${displayCount} (${rate.toFixed(1)}%)`;
    },
    [formatNumber]
  );

  // 엑셀 다운로드용 전체 데이터 (현재 정렬 상태 그대로, 페이지네이션 무시)
  const allRowsDataForExcel = useMemo(() => {
    return dateSummaries.map((summary) => {
      const row = [
        summary.date,
        formatNumber(summary.dau),
        formatNumber(summary.newUserCount),
        ...retentionLabels.map((key) =>
          formatRetentionWithRate(summary.retention[key] ?? 0, summary.newUserCount)
        )
      ];
      return row;
    });
  }, [dateSummaries, retentionLabels, formatNumber, formatRetentionWithRate]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            fontSize: '0.85rem',
            color: '#4b5563',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '999px',
                backgroundColor: '#111827',
              }}
            />
            <Box component="span">방문 유저 수</Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '999px',
                backgroundColor: '#2563eb',
              }}
            />
            <Box component="span">캐시 적립 유저 수</Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <TableActionsMenu
            tableRef={tableRef}
            allRowsData={allRowsDataForExcel}
            filename={filename}
            sheetName="리텐션"
          />
        </Box>
      </Box>
      <TableContainer
        sx={{
          backgroundColor: '#fff',
          borderRadius: 2,
        overflowX: 'auto'
      }}
    >
      <Table ref={tableRef} stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              <TableSortLabel
                active
                direction={sortDirection}
                onClick={toggleSort}
              >
                날짜
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>DAU</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>신규 유저수</TableCell>
            {retentionLabels.map((key) => (
              <TableCell
                key={key}
                align="right"
                sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}
              >
                {key}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {dateSummaries.map(summary => (
            <TableRow key={summary.date}>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{summary.date}</TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {formatNumber(summary.dau)}
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: 0.3,
                    fontSize: '0.9rem',
                  }}
                >
                  <Box component="span" sx={{ lineHeight: 1.15 }}>
                    {formatNumber(summary.newUserCount)}
                  </Box>
                  {summary.newUserCashCount > 0 && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: '0.8rem',
                        color: '#2563eb',
                      }}
                    >
                      {`${formatNumber(summary.newUserCashCount)} (${summary.newUserCount > 0 ? ((summary.newUserCashCount / summary.newUserCount) * 100).toFixed(1) : '0.0'}%)`}
                    </Box>
                  )}
                </Box>
              </TableCell>
              {retentionLabels.map((key) => {
                const count = summary.retention[key] ?? 0;
                const cashCount = summary.cashRetention?.[key] ?? 0;
                const base = summary.newUserCount ?? 0;
                const rate = base > 0 ? (count / base) * 100 : 0;
                const cashRate = base > 0 ? (cashCount / base) * 100 : 0;

                let bgColor = 'transparent';
                if (rate > 0) {
                  // 20~15, 15~10, 10~7, 7~4, 4~1, 1~0 구간으로 세분화
                  if (rate <= 1) {
                    // 0~1%
                    bgColor = '#EFF6FF';
                  } else if (rate <= 4) {
                    // 1~4%
                    bgColor = '#E0F2FE';
                  } else if (rate <= 7) {
                    // 4~7%
                    bgColor = '#DBEAFE';
                  } else if (rate <= 10) {
                    // 7~10%
                    bgColor = '#BFDBFE';
                  } else if (rate <= 15) {
                    // 10~15%
                    bgColor = '#93C5FD';
                  } else if (rate <= 20) {
                    // 15~20%
                    bgColor = '#60A5FA';
                  } else {
                    // 20% 이상은 가장 진한 단계와 동일
                    bgColor = '#60A5FA';
                  }
                }

                return (
                  <TableCell
                    key={key}
                    align="right"
                    sx={{
                      whiteSpace: 'nowrap',
                      backgroundColor: bgColor,
                      color: '#111827', // 기본 텍스트는 진한 색
                      transition: 'background-color 0.2s ease-out',
                      paddingY: 0.9,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: 0.3,
                        fontSize: '0.9rem', // 기본 숫자+퍼센트 조금 더 키움
                      }}
                    >
                      <Box component="span" sx={{ lineHeight: 1.15 }}>
                        {formatRetentionWithRate(count, summary.newUserCount)}
                      </Box>
                      {cashCount > 0 && (
                        <Box
                          component="span"
                          sx={{
                            fontSize: '0.8rem', // 파란 글씨도 한 단계 키움
                            color: '#2563eb', // 파란 글씨
                          }}
                        >
                          {`${formatNumber(cashCount)} (${cashRate.toFixed(1)}%)`}
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {/* <TablePagination
        component="div"
        count={dateSummaries.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[rowsPerPage]}
      /> */}
    </TableContainer>
    </Box>
  );
}
